import React, { useState, useRef, useEffect } from 'react';
import { Badge, Button, Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import { FiMenu, FiGrid, FiUser, FiMail, FiSettings, FiLogOut } from 'react-icons/fi';
import { useAuthStore } from '@features/auth/store/authStore';
import { useLogout } from '@features/auth/hooks/logout';
import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';
import { usePendingInviteCount } from '@features/user/invitations/hooks/usePendingInviteCount';
import { sendRequest } from '@infrastructure/api/api';
import LogoutConfirmModal from './LogoutConfirmModal';

type EntityScope = 'NATION' | 'DISTRICT' | 'CHURCH';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  showUserAvatar?: boolean;
  customActions?: React.ReactNode | React.ReactNode[];
  onMobileMenuToggle?: () => void;
  showMobileMenuButton?: boolean;
  /** When set, show entity type badge + entity name on the left of the bar */
  entityScope?: EntityScope;
  entityName?: string;
}

function getScopeBadgeVariant(scope: EntityScope): 'danger' | 'info' | 'success' | 'secondary' {
  switch (scope) {
    case 'NATION': return 'danger';
    case 'DISTRICT': return 'info';
    case 'CHURCH': return 'success';
    default: return 'secondary';
  }
}

function getScopeLabel(scope: EntityScope): string {
  switch (scope) {
    case 'NATION': return 'Nation';
    case 'DISTRICT': return 'District';
    case 'CHURCH': return 'Church';
    default: return scope;
  }
}

function getCachedHeaderDisplayName(ownerKey: string | null): string | null {
  if (!ownerKey) return null;

  const cachedOwner = sessionStorage.getItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER);
  const cachedDisplayName = sessionStorage.getItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME);

  if (!cachedDisplayName) return null;
  if (cachedOwner === ownerKey) return cachedDisplayName;

  // Clear legacy or mismatched cache so an old user's name can't leak into a new session.
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER);
  return null;
}

let headerDisplayNameInFlight: Promise<string | null> | null = null;

async function loadHeaderDisplayName(
  fallbackName: string,
  ownerKey: string | null
): Promise<string | null> {
  const cachedDisplayName = getCachedHeaderDisplayName(ownerKey);
  if (cachedDisplayName) return cachedDisplayName;

  if (!headerDisplayNameInFlight) {
    headerDisplayNameInFlight = Promise.allSettled([
      sendRequest<any, any>('GET', 'user/profile'),
      sendRequest<any, any>('GET', 'user/title/query?all=true'),
    ]).then(([profileResp, titlesResp]) => {
      const profile = profileResp.status === 'fulfilled' && profileResp.value?.isSuccess?.()
        ? profileResp.value.getData()
        : null;
      if (!profile) return fallbackName;

      const titlesData = titlesResp.status === 'fulfilled' && titlesResp.value?.isSuccess?.()
        ? titlesResp.value.getData()
        : null;
      const titlesList = titlesData?.data ?? titlesData ?? [];

      const titleCode = profile.title_code;
      const titleMatch = Array.isArray(titlesList)
        ? titlesList.find((t: any) => t.title_code === titleCode)
        : null;
      const titleName = titleMatch?.title_name || '';

      const fname = profile.fname || '';
      const lname = profile.lname || '';
      const parts = [titleName, fname, lname].filter(Boolean);
      const fullName = parts.length > 0 ? parts.join(' ') : fallbackName;

      if (ownerKey && fullName) {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME, fullName);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER, ownerKey);
      }
      return fullName;
    }).catch(() => fallbackName).finally(() => {
      headerDisplayNameInFlight = null;
    });
  }

  return headerDisplayNameInFlight;
}

const Header: React.FC<HeaderProps> = ({
  title = "Welcome",
  subtitle = "Overview • Status • Shortcuts",
  showUserAvatar = true,
  customActions,
  onMobileMenuToggle,
  showMobileMenuButton = false,
  entityScope,
  entityName,
}) => {
  const showEntityBar = Boolean(entityScope);
  const accessToken = useAuthStore((state) => state.accessToken);
  const logout = useLogout();
  const authUser = accessToken ? useAuthStore.getState().getAuthUser() : null;
  const jwtName = authUser?.getFullName() ?? 'User';
  const userEmail = authUser?.getEmail() ?? '';
  const headerDisplayNameOwner = userEmail || jwtName || null;
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [displayName, setDisplayName] = useState(() => {
    return getCachedHeaderDisplayName(headerDisplayNameOwner) || jwtName;
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { total: pendingInviteCount } = usePendingInviteCount();

  useEffect(() => {
    setDisplayName(getCachedHeaderDisplayName(headerDisplayNameOwner) || jwtName);
  }, [headerDisplayNameOwner, jwtName]);

  // Fetch profile + titles to build full display name (cached in sessionStorage)
  useEffect(() => {
    if (getCachedHeaderDisplayName(headerDisplayNameOwner)) return;

    let cancelled = false;

    void loadHeaderDisplayName(jwtName, headerDisplayNameOwner).then((fullName) => {
      if (!cancelled && fullName) {
        setDisplayName(fullName);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [headerDisplayNameOwner, jwtName]);

  // Listen for profile updates from the dedicated profile flow
  useEffect(() => {
    const handleProfileUpdate = () => {
      const cached = getCachedHeaderDisplayName(headerDisplayNameOwner);
      setDisplayName(cached || jwtName);
    };
    window.addEventListener('ntcog-profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('ntcog-profile-updated', handleProfileUpdate);
  }, [headerDisplayNameOwner, jwtName]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="ntcog-header" role="banner">
      <Container fluid className="py-3 px-1 px-sm-4">
      <Row className="align-items-center ntcog-header__row">
        <Col xs="auto" className="d-flex align-items-center ps-lg-0 ps-2 ntcog-header__left">
          {showMobileMenuButton && onMobileMenuToggle && (
            <Button
              variant="outline-primary"
              onClick={onMobileMenuToggle}
              className="me-3 d-lg-none border-0"
              size="sm"
              aria-label="Open menu"
              aria-expanded={false}
              title="Open navigation menu"
            >
              <FiMenu size={16} aria-hidden />
            </Button>
          )}
          {showEntityBar && entityScope && (
            <Link
              to="/entities"
              className="d-flex align-items-center gap-2 me-3 me-md-4 text-decoration-none ntcog-header__entity min-w-0"
              style={{ cursor: 'pointer', color: 'inherit' }}
              title="Click to switch accounts"
            >
              <div className="d-flex align-items-center gap-2 min-w-0" style={{ lineHeight: 1.3 }}>
                {/* <Badge bg={getScopeBadgeVariant(entityScope)} className="text-nowrap flex-shrink-0" style={{ fontSize: '0.65rem' }}>
                  {getScopeLabel(entityScope)}
                </Badge> */}
                {entityName && (
                  <span
                    className="entity-name fw-bold text-body text-truncate d-block flex-grow-1"
                    style={{ fontSize: '1.2rem' }}
                  >
                    {entityName}
                  </span>
                )}
              </div>
            </Link>
          )}
          {/*((!showEntityBar) || title || subtitle) && (
            <div className="ntcog-header__title-block min-w-0">
              <h1 className="h5 h6-lg mb-0 fw-bold text-truncate" style={{ maxWidth: 'min(200px, 50vw)' }}>
                {title.includes('{userName}') ? title.replace('{userName}', jwtName) : title}
              </h1>
              <p className="text-muted mb-0 d-none d-sm-block" style={{ fontSize: 14 }}>
                {subtitle}
              </p>
            </div>
          )*/}
        </Col>
        <Col xs="auto" className="ms-auto ntcog-header__right flex-shrink-0">
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            {customActions}
            {showUserAvatar && (
              <div className="position-relative" ref={dropdownRef}>
            <Button
              variant="link"
              className="p-0 text-decoration-none d-flex align-items-center gap-2"
              style={{ border: 'none', background: 'none' }}
              onClick={() => setShowDropdown(!showDropdown)}
              aria-expanded={showDropdown}
              aria-haspopup="true"
              aria-label={`User menu for ${displayName}`}
              title="Account menu — profile, settings & sign out"
            >
                  <div className="text-end d-none d-sm-block">
                    <div className="fw-semibold" style={{ fontSize: '0.85rem', lineHeight: 1.2, color: 'var(--ntcog-text)' }}>
                      {displayName}
                    </div>
                    {userEmail && (
                      <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                        {userEmail}
                      </div>
                    )}
                  </div>
                  <div className="position-relative d-inline-flex">
                  <FaUserCircle
                    className="text-primary"
                    size={28}
                    aria-hidden
                  />
                  {pendingInviteCount > 0 && (
                    <Badge
                      bg="danger"
                      pill
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: '0.65rem', minWidth: '1.1rem' }}
                      aria-label={`${pendingInviteCount} pending invitations`}
                    >
                      {pendingInviteCount > 9 ? '9+' : pendingInviteCount}
                    </Badge>
                  )}
                  </div>
                </Button>
                
                {showDropdown && (
                  <div
                    className="position-absolute ntcog-dropdown"
                    style={{
                      top: '100%',
                      right: 0,
                      zIndex: 1000,
                      marginTop: '0.35rem',
                    }}
                  >
                    <Link
                      to="/entities"
                      className="ntcog-dropdown__item d-flex align-items-center text-decoration-none"
                      onClick={() => setShowDropdown(false)}
                    >
                      <FiGrid className="me-2" size={16} />
                      My Workspaces
                    </Link>
                    <Link
                      to="/user/profile"
                      className="ntcog-dropdown__item d-flex align-items-center text-decoration-none"
                      onClick={() => setShowDropdown(false)}
                    >
                      <FiUser className="me-2" size={16} />
                      Profile
                    </Link>
                    <Link
                      to="/pending-invitations"
                      className="ntcog-dropdown__item d-flex align-items-center justify-content-between text-decoration-none"
                      onClick={() => setShowDropdown(false)}
                    >
                      <span className="d-flex align-items-center">
                        <FiMail className="me-2" size={16} />
                        Invites
                      </span>
                      {pendingInviteCount > 0 && (
                        <Badge bg="danger" pill style={{ fontSize: '0.7rem' }}>
                          {pendingInviteCount}
                        </Badge>
                      )}
                    </Link>
                    <Link
                      to="/user/settings"
                      className="ntcog-dropdown__item d-flex align-items-center text-decoration-none"
                      onClick={() => setShowDropdown(false)}
                    >
                      <FiSettings className="me-2" size={16} />
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="ntcog-dropdown__item ntcog-dropdown__item--danger d-flex align-items-center text-danger"
                      onClick={() => {
                        setShowDropdown(false);
                        setShowLogoutConfirm(true);
                      }}
                    >
                      <FiLogOut className="me-2" size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>
      <LogoutConfirmModal
        show={showLogoutConfirm}
        onConfirm={() => {
          setShowLogoutConfirm(false);
          void logout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </Container>
    </header>
  );
};

export default Header;
